import { useEffect, useRef, useState } from 'preact/hooks';
import { useOddball } from '../midi-context';
import type { SynthMode } from '../midi-context';
import { getAudioContext, createReverbBuffer, createNoiseBuffer } from '../audio/audio-engine';
import { playSoundfontNote, freqToMidi, isInstrumentLoaded, getCurrentProgram } from '../audio/soundfont-player';
import type { TapEvent, ShakeEvent, TwistEvent, CCUpdateEvent } from '../../src/types';
import { OddballCC } from '../../src/types';

const SCALE = [
  261.63, 293.66, 329.63, 392.00, 440.00,
  523.25, 587.33, 659.25, 783.99, 880.00,
];

const GESTURE_COLORS: Record<string, [number, number, number]> = {
  tap: [255, 180, 50],
  shake: [80, 140, 255],
  twist: [180, 80, 255],
};

interface EngineState {
  ctx: AudioContext;
  masterGain: GainNode;
  dryGain: GainNode;
  wetGain: GainNode;
  convolver: ConvolverNode;
  panner: StereoPannerNode;
  noiseBuffer: AudioBuffer;
  scaleIndex: number;
  lastShakeTime: number;
  lastTwistTime: number;
  twistOsc: OscillatorNode | null;
  twistFilter: BiquadFilterNode | null;
  twistGain: GainNode | null;
  twistLfo: OscillatorNode | null;
  twistLfoGain: GainNode | null;
  twistFadeTimeout: number;
  octaveShift: number;
}

function createEngine(): EngineState {
  const ctx = getAudioContext();
  const masterGain = ctx.createGain();
  masterGain.gain.value = 0.8;

  const panner = ctx.createStereoPanner();
  const dryGain = ctx.createGain();
  dryGain.gain.value = 0.75;
  const wetGain = ctx.createGain();
  wetGain.gain.value = 0.25;

  const convolver = ctx.createConvolver();
  convolver.buffer = createReverbBuffer(ctx, 1.5, 2);

  masterGain.connect(panner);
  panner.connect(dryGain);
  panner.connect(convolver);
  convolver.connect(wetGain);
  dryGain.connect(ctx.destination);
  wetGain.connect(ctx.destination);

  return {
    ctx, masterGain, dryGain, wetGain, convolver, panner,
    noiseBuffer: createNoiseBuffer(ctx, 0.5),
    scaleIndex: 0, lastShakeTime: 0, lastTwistTime: 0,
    twistOsc: null, twistFilter: null, twistGain: null,
    twistLfo: null, twistLfoGain: null, twistFadeTimeout: 0,
    octaveShift: 0,
  };
}

function playTap(e: EngineState, velocity: number, synthMode: SynthMode) {
  const { ctx, masterGain } = e;
  const now = ctx.currentTime;
  const vol = (velocity / 127) * 0.4;
  const freq = SCALE[e.scaleIndex % SCALE.length] * Math.pow(2, e.octaveShift);
  e.scaleIndex++;

  if (synthMode === 'soundfont' && isInstrumentLoaded(getCurrentProgram())) {
    playSoundfontNote(masterGain, freqToMidi(freq), 0.35, vol);
  } else {
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = freq;
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = freq * 2;

    const g1 = ctx.createGain();
    const g2 = ctx.createGain();
    g1.gain.setValueAtTime(0, now);
    g1.gain.linearRampToValueAtTime(vol, now + 0.005);
    g1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    g2.gain.setValueAtTime(0, now);
    g2.gain.linearRampToValueAtTime(vol * 0.35, now + 0.005);
    g2.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

    osc1.connect(g1).connect(masterGain);
    osc2.connect(g2).connect(masterGain);
    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 0.4);
    osc2.stop(now + 0.3);
  }
}

function playShake(e: EngineState, velocity: number) {
  const now = performance.now();
  if (now - e.lastShakeTime < 20) return;
  e.lastShakeTime = now;

  const { ctx, masterGain, noiseBuffer } = e;
  const t = ctx.currentTime;
  const vol = (velocity / 127) * 0.3;

  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 2000 + (velocity / 127) * 6000;
  bp.Q.value = 1.2;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + 0.003);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);

  src.connect(bp).connect(g).connect(masterGain);
  src.start(t);
  src.stop(t + 0.06);
}

function playTwist(e: EngineState, velocity: number) {
  const now = performance.now();
  if (now - e.lastTwistTime < 30) return;
  e.lastTwistTime = now;

  const { ctx, masterGain } = e;
  const t = ctx.currentTime;
  const velNorm = velocity / 127;

  if (!e.twistOsc) {
    e.twistOsc = ctx.createOscillator();
    e.twistOsc.type = 'sawtooth';
    e.twistOsc.frequency.value = 130.81 * Math.pow(2, e.octaveShift);
    e.twistFilter = ctx.createBiquadFilter();
    e.twistFilter.type = 'lowpass';
    e.twistFilter.frequency.value = 300;
    e.twistFilter.Q.value = 4;
    e.twistGain = ctx.createGain();
    e.twistGain.gain.value = 0;
    e.twistLfo = ctx.createOscillator();
    e.twistLfo.type = 'sine';
    e.twistLfo.frequency.value = 0.8;
    e.twistLfoGain = ctx.createGain();
    e.twistLfoGain.gain.value = 200;

    e.twistLfo.connect(e.twistLfoGain).connect(e.twistFilter.frequency);
    e.twistOsc.connect(e.twistFilter).connect(e.twistGain).connect(masterGain);
    e.twistLfo.start();
    e.twistOsc.start();
  }

  e.twistFilter!.frequency.cancelScheduledValues(t);
  e.twistFilter!.frequency.setTargetAtTime(300 + velNorm * 2500, t, 0.05);
  e.twistOsc!.detune.cancelScheduledValues(t);
  e.twistOsc!.detune.setTargetAtTime(velNorm * 30 - 15, t, 0.05);
  e.twistGain!.gain.cancelScheduledValues(t);
  e.twistGain!.gain.setTargetAtTime(0.06 + velNorm * 0.06, t, 0.04);

  clearTimeout(e.twistFadeTimeout);
  e.twistFadeTimeout = window.setTimeout(() => fadeTwist(e), 800);
}

function fadeTwist(e: EngineState) {
  if (!e.twistOsc) return;
  const t = e.ctx.currentTime;
  e.twistGain!.gain.cancelScheduledValues(t);
  e.twistGain!.gain.setTargetAtTime(0, t, 0.15);
  setTimeout(() => {
    if (e.twistOsc) {
      e.twistOsc.stop();
      e.twistLfo!.stop();
      e.twistOsc = null;
      e.twistFilter = null;
      e.twistGain = null;
      e.twistLfo = null;
      e.twistLfoGain = null;
    }
  }, 1200);
}

export function PentatonicToy() {
  const { parser, connected, octaveOffset, synthMode } = useOddball();
  const engineRef = useRef<EngineState | null>(null);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  const octaveRef = useRef(0);
  const synthModeRef = useRef<SynthMode>('oscillator');
  const [vizState, setVizState] = useState<{ gesture: string; velocity: number; time: number } | null>(null);

  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { octaveRef.current = octaveOffset; }, [octaveOffset]);
  useEffect(() => { synthModeRef.current = synthMode; }, [synthMode]);
  useEffect(() => {
    const eng = engineRef.current;
    if (eng) eng.masterGain.gain.setTargetAtTime(connected ? 0.8 : 0, eng.ctx.currentTime, 0.05);
  }, [connected]);

  useEffect(() => {
    const eng = createEngine();
    engineRef.current = eng;

    const onTap = (evt: TapEvent) => {
      if (!mutedRef.current) playTap(eng, evt.velocity, synthModeRef.current);
      setVizState({ gesture: 'tap', velocity: evt.velocity, time: performance.now() });
    };
    const onShake = (evt: ShakeEvent) => {
      if (!mutedRef.current) playShake(eng, evt.velocity);
      setVizState({ gesture: 'shake', velocity: evt.velocity, time: performance.now() });
    };
    const onTwist = (evt: TwistEvent) => {
      if (!mutedRef.current) playTwist(eng, evt.velocity);
      setVizState({ gesture: 'twist', velocity: evt.velocity, time: performance.now() });
    };
    const onCC = (evt: CCUpdateEvent) => {
      eng.octaveShift = octaveRef.current;
      if (mutedRef.current) return;
      switch (evt.cc) {
        case OddballCC.SpinSmoothed:
          if (eng.twistLfo) {
            const rate = 0.3 + (evt.value / 127) * 4;
            eng.twistLfo.frequency.setTargetAtTime(rate, eng.ctx.currentTime, 0.05);
          }
          break;
        case OddballCC.FreeFall:
          if (evt.value > 10 && eng.twistOsc) {
            const t = eng.ctx.currentTime;
            eng.twistOsc.detune.cancelScheduledValues(t);
            eng.twistOsc.detune.setTargetAtTime((evt.value / 127) * 200, t, 0.1);
          }
          break;
        case OddballCC.OrientX:
          eng.panner.pan.setTargetAtTime((evt.value - 64) / 64, eng.ctx.currentTime, 0.05);
          break;
        case OddballCC.OrientY: {
          const norm = evt.value / 127;
          const t = eng.ctx.currentTime;
          eng.wetGain.gain.setTargetAtTime(0.05 + norm * 0.45, t, 0.05);
          eng.dryGain.gain.setTargetAtTime(0.95 - norm * 0.45, t, 0.05);
          break;
        }
      }
    };

    parser.on('tap', onTap);
    parser.on('shake', onShake);
    parser.on('twist', onTwist);
    parser.on('cc', onCC);

    return () => {
      parser.off('tap', onTap);
      parser.off('shake', onShake);
      parser.off('twist', onTwist);
      parser.off('cc', onCC);
      fadeTwist(eng);
      eng.masterGain.disconnect();
    };
  }, [parser]);

  // Compute visual state
  const active = vizState && performance.now() - vizState.time < 500;
  const color = active ? GESTURE_COLORS[vizState!.gesture] ?? [200, 200, 200] : [50, 50, 50];
  const velNorm = active ? vizState!.velocity / 127 : 0;
  const glow = active ? 30 + velNorm * 50 : 40;
  const opacity = active ? 0.3 + velNorm * 0.4 : 0.05;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', paddingTop: '40px' }}>
      <h2 style={{ fontSize: '1rem', fontWeight: 300, color: '#888' }}>Pentatonic Toy</h2>

      {/* Glowing circle */}
      <div
        style={{
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: `radial-gradient(circle at 40% 35%, rgba(${color[0]},${color[1]},${color[2]},${opacity}), #1a1a1a)`,
          boxShadow: `0 0 ${glow}px rgba(${color[0]},${color[1]},${color[2]},${opacity * 0.5})`,
          transition: 'all 0.15s',
        }}
      />

      {/* Gesture label */}
      <div style={{ fontSize: '1.4rem', fontWeight: 300, letterSpacing: '0.1em', color: active ? `rgb(${color[0]},${color[1]},${color[2]})` : '#333', minHeight: '1.8em', transition: 'color 0.2s' }}>
        {active ? vizState!.gesture.toUpperCase() : ''}
      </div>

      {/* Mute toggle */}
      <button
        onClick={() => setMuted(!muted)}
        style={{
          padding: '6px 16px', borderRadius: '12px',
          border: '1px solid ' + (muted ? '#a44' : '#444'),
          background: muted ? '#311' : 'transparent',
          color: muted ? '#a44' : '#888',
          cursor: 'pointer', fontSize: '0.8rem',
        }}
      >
        {muted ? 'Unmute' : 'Mute'}
      </button>
    </div>
  );
}
